import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import React, { useMemo, useState } from 'react'
import { User } from '../../models/User'
import { TableColumn } from 'react-data-table-component'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Form, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'
import { EditContactModal } from './EditContactModal'
import { DeleteContactModal } from './DeleteContactModal'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'

import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Roles } from '../../components/Roles/Roles'
import { UserAvatarInline } from '../../components/UserAvatar/UserAvatarInline'
import { useSearchExternalUsersQuery } from '../../store/externalUser/externalUser'
import { useDebounce } from 'use-debounce'
import { useCookies } from 'react-cookie'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { AddContactModal } from './AddContactModal'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Button } from '../../components/Button/Button'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { ContactActionUpgradeBadge } from './Contact'

import NoContactsFoundIcon from '../../assets/images/no-contacts-found-icon.png'
import { useGetAllUsersQuery } from '../../store/user/user'
import { skipToken } from '@reduxjs/toolkit/query'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { DELETED_USER_LABEL } from './constants'
import classNames from 'classnames'
import { LinkButton } from '../../components/Button/LinkButton'
import { FeatureAdvertisement } from '../../components/FeatureAdvertisement/FeatureAdvertisement'

const SHOW_ONLY_MY_CONTACTS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const _SEARCH_TERM_MIN_LENGTH = 3
const _SEARCH_TERM_PARAM_KEY = 'term'

export const Contacts = () => {
  useDocumentTitle('Contacts')

  const [searchParams, setSearchParams] = useSearchParams()
  const { userId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const { hasQuota } = useZocksQuota()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(userId)

  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)
  const [showEditContactModal, setShowEditContactModal] = useState<boolean>(modal === 'edit')
  const [showDeleteContactModal, setShowDeleteContactModal] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get(_SEARCH_TERM_PARAM_KEY) ?? '')
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null)
  const [lastDebouncedTerm, setLastDebouncedTerm] = useState<string>('')

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400)

  if (lastDebouncedTerm !== debouncedSearchTerm) {
    setLastDebouncedTerm(debouncedSearchTerm)
    if (!debouncedSearchTerm) {
      setActiveSearchTerm(null)
    } else if (debouncedSearchTerm.length >= _SEARCH_TERM_MIN_LENGTH) {
      setActiveSearchTerm(debouncedSearchTerm)
    }
  }

  const [cookies, setCookie] = useCookies(['showOnlyMyContacts'])
  const [showOnlyMyContacts, setShowOnlyMyContacts] = useState<boolean>(cookies.showOnlyMyContacts ?? false)

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  const {
    data: contactsResponse,
    isFetching,
    refetch: refetchContacts,
  } = useSearchExternalUsersQuery({
    accountId,
    ...(activeSearchTerm ? { term: activeSearchTerm } : {}),
    ...(showOnlyMyContacts && { ownerId: currentUserId }),
    pageRequest: { page: page - 1, size: perPage },
    orderBy: 'lastName',
  })

  const contacts = useMemo(() => contactsResponse?.data ?? [], [contactsResponse?.data])

  const ownerIds = useMemo(() => [...new Set(contacts.map((c) => c.owner?.ownerId).filter((id): id is string => !!id))].sort(), [contacts])

  const { currentData: ownerUsersResponse } = useGetAllUsersQuery(ownerIds.length > 0 ? { accountId, ids: ownerIds, identityTypes: ['idp'] } : skipToken)

  const ownerUsers: Record<string, User> = useMemo(
    () => Object.fromEntries((ownerUsersResponse?.data ?? []).map((user) => [user.id, user])),
    [ownerUsersResponse?.data],
  )

  const getOwner = (contact: User) => (contact.owner ? ownerUsers[contact.owner.ownerId] : undefined)

  const getOwnerLabel = (contact: User, owner: User | undefined) => {
    if (!contact.owner) {
      return 'Not set'
    }
    if (owner) {
      return getUserDisplayName(owner)
    }
    return ownerUsersResponse ? DELETED_USER_LABEL : ''
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const _handleOnClickAddNewContact = () => setShowAddContactModal(true)

  const handleOnClickAddNewContact = tracked(_handleOnClickAddNewContact, { eventName: TrackEvent.ADD_CONTACT_CLICKED })

  const _handleOnClickUploadDocument = () => {
    navigate(`/library/manage-contacts/contacts/documents/process`)
  }

  const handleOnClickUploadDocument = tracked(_handleOnClickUploadDocument, { eventName: TrackEvent.DOCUMENT_PROSPECTING_STARTED })

  const handleOnClickEditContact = (user: User) => {
    setSelectedUserId(user.id)
    setShowEditContactModal(true)
    navigate(`/library/manage-contacts/contacts/${user.id}/edit`, { replace: true })
  }

  const handleOnClickShowContact = (user: User) => {
    navigate(`/library/manage-contacts/contacts/${user.id}`)
  }

  const handleOnClickDeleteContact = (user: User) => {
    setSelectedUserId(user.id)
    setShowDeleteContactModal(true)
  }

  const handleAddContactModalSave = () => {
    setShowAddContactModal(false)
    navigate(0)
  }

  const handleAddContactModalCancel = () => {
    setShowAddContactModal(false)
  }

  const handleEditContactModalSave = () => {
    setShowEditContactModal(false)
    setSelectedUserId(null)
    refetchContacts()
    navigate(`/library/manage-contacts/`, { replace: true })
  }

  const handleEditContactModalCancel = () => {
    setShowEditContactModal(false)
    setSelectedUserId(null)
    navigate(`/library/manage-contacts/`, { replace: true })
  }

  const handleDeleteContactModalDelete = () => {
    setShowDeleteContactModal(false)
    setSelectedUserId(null)
    refetchContacts()
  }

  const handleDeleteContactModalCancel = () => {
    setShowDeleteContactModal(false)
    setSelectedUserId(null)
  }

  const handleSetShowOnlyMyContacts = (showOnlyMyContacts: boolean) => {
    setShowOnlyMyContacts(showOnlyMyContacts)
    setCookie('showOnlyMyContacts', showOnlyMyContacts, {
      path: '/',
      maxAge: SHOW_ONLY_MY_CONTACTS_COOKIE_MAX_AGE,
    })
  }

  const handleSearchTermChange = (searchTerm: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (!searchTerm) {
      newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
    } else {
      newSearchParams.set(_SEARCH_TERM_PARAM_KEY, searchTerm)
    }

    setSearchTerm(searchTerm)
    setSearchParams(newSearchParams)
  }

  const handleClearSearch = () => {
    handleSearchTermChange('')
    handleSetShowOnlyMyContacts(false)
  }

  const columns: TableColumn<User>[] = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <NavLink className='paragraph-small text-decoration-underline zocks-text-gray-900' to={`/library/manage-contacts/contacts/${row.id}`}>
          {row.name}
        </NavLink>
      ),
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => <span className='paragraph-small zocks-text-gray-900'>{row.email}</span>,
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.roles.map((role) => role.id)),
      sortable: true,
      cell: (row) => <Roles userRoleIds={row.roles.map((role) => role.id)} className='paragraph-small zocks-text-gray-900' />,
    },
    {
      name: 'Owner',
      selector: (row) => getOwnerLabel(row, getOwner(row)),
      sortable: true,
      wrap: true,
      cell: (row) => {
        const owner = getOwner(row)
        const label = getOwnerLabel(row, owner)
        if (!owner) {
          return <span className={classNames({ 'zocks-text-gray-600': row.owner })}>{label}</span>
        }
        return (
          <div className='d-flex align-items-center gap-2'>
            <UserAvatarInline user={owner} />
            <LinkButton variant='link' className='button-link-small' to={`/account/manage-team/users/${owner.id}`}>
              {label}
            </LinkButton>
          </div>
        )
      },
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'ext_user:read',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickShowContact(row)}>
              Show
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'ext_user:update',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditContact(row)}>
              Edit
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'ext_user:delete',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteContact(row)}>
              Delete
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  return (
    <Container className='p-0 d-flex flex-column zocks-gap-2'>
      <AddContactModal show={showAddContactModal} onSave={handleAddContactModalSave} onCancel={handleAddContactModalCancel} />
      {selectedUserId ? (
        <EditContactModal userId={selectedUserId} show={showEditContactModal} onSave={handleEditContactModalSave} onCancel={handleEditContactModalCancel} />
      ) : null}
      {selectedUserId ? (
        <DeleteContactModal
          user={contacts.find((user) => user.id === selectedUserId)}
          show={showDeleteContactModal}
          onDelete={handleDeleteContactModalDelete}
          onCancel={handleDeleteContactModalCancel}
        />
      ) : null}
      <Row>
        <Col className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
          <ScopedBlock
            scope={{
              scope: 'ext_user:create',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <div className='d-flex align-items-center gap-3'>
              <Button variant='primary' className='px-3 py-2' onClick={handleOnClickAddNewContact}>
                <i className='bi bi-plus-circle' />
                Add manually
              </Button>
              <FeatureAdvertisement bypass={documentSummaryEnabled}>
                <Button variant='grayGhost' className='px-3 py-2 position-relative' onClick={handleOnClickUploadDocument}>
                  {!documentSummaryEnabled ? <ContactActionUpgradeBadge /> : null}
                  <i className='bi bi-file-earmark-richtext' />
                  Create from document
                </Button>
              </FeatureAdvertisement>
            </div>
          </ScopedBlock>
          <div className='d-flex align-items-center gap-4'>
            <Form.Check
              checked={showOnlyMyContacts}
              onChange={(e) => handleSetShowOnlyMyContacts(e.currentTarget.checked)}
              type='checkbox'
              id='show-only-my-contacts'
              label='Only your contacts'
            />
            <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-500 zocks-px-0.75 contacts-search-bar-wrapper'>
              <Form.Control
                type='text'
                value={searchTerm}
                onChange={(e) => handleSearchTermChange(e.currentTarget.value)}
                placeholder='Search in contacts'
                className='border-0 px-0'
              />
              <i className='bi bi-search' />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={contacts}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={contactsResponse?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoContactsFound showClearButton={searchTerm.length > 0} onClear={handleClearSearch} />}
          />
        </Col>
      </Row>
    </Container>
  )
}

interface NoContactsFoundProps {
  showClearButton: boolean
  onClear: () => void
}

const NoContactsFound: React.FC<NoContactsFoundProps> = ({ showClearButton, onClear }) => {
  return (
    <div className='d-flex flex-column align-items-center gap-3 zocks-py-2.0 w-100 contacts-no-contacts-found-container'>
      <img alt='No contacts found' src={NoContactsFoundIcon} />
      <div className='text-center'>
        <h5>No contacts found</h5>
        <span className='paragraph-medium zocks-text-gray-600'>Try different search terms or add a new contact.</span>
      </div>
      {showClearButton ? (
        <Button variant='grayGhost' className='button-small px-2 py-1' onClick={onClear}>
          Clear search
        </Button>
      ) : null}
    </div>
  )
}
